import { useState } from "react"
import { FileText, Plus, Pencil, Trash2, Copy, Eye } from "lucide-react"
import {
  useEmailTemplates,
  useCreateEmailTemplate,
  useUpdateEmailTemplate,
  useDeleteEmailTemplate,
  useRenderEmailTemplate,
  type EmailTemplate,
} from "@/hooks/use-email-templates"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { toast } from "sonner"

// ── Categories ──────────────────────────────────────────────────

const CATEGORIES = [
  { key: "", label: "Wszystkie" },
  { key: "sales", label: "Sprzedaż" },
  { key: "support", label: "Obsługa" },
  { key: "followup", label: "Follow-up" },
  { key: "general", label: "Ogólne" },
]

const CATEGORY_LABELS: Record<string, string> = {
  sales: "Sprzedaż",
  support: "Obsługa",
  followup: "Follow-up",
  general: "Ogólne",
}

// ── Template Form ───────────────────────────────────────────────

interface TemplateFormData {
  name: string
  subject: string
  body_html: string
  body_text: string
  category: string
  is_shared: boolean
}

const EMPTY_FORM: TemplateFormData = {
  name: "",
  subject: "",
  body_html: "",
  body_text: "",
  category: "general",
  is_shared: false,
}

const MERGE_FIELDS = [
  { key: "{{company.name}}", label: "Firma — Nazwa" },
  { key: "{{company.nip}}", label: "Firma — NIP" },
  { key: "{{company.city}}", label: "Firma — Miasto" },
  { key: "{{contact.first_name}}", label: "Kontakt — Imię" },
  { key: "{{contact.last_name}}", label: "Kontakt — Nazwisko" },
  { key: "{{contact.email}}", label: "Kontakt — Email" },
  { key: "{{deal.name}}", label: "Szansa — Nazwa" },
  { key: "{{deal.value}}", label: "Szansa — Wartość" },
  { key: "{{user.name}}", label: "Użytkownik — Imię i nazwisko" },
  { key: "{{user.email}}", label: "Użytkownik — Email" },
  { key: "{{date}}", label: "Dzisiejsza data" },
]

function TemplateFormDialog({
  open,
  onOpenChange,
  initial,
  onSave,
  isSaving,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  initial: TemplateFormData
  onSave: (data: TemplateFormData) => void
  isSaving: boolean
}) {
  const [form, setForm] = useState<TemplateFormData>(initial)
  const [showMergeFields, setShowMergeFields] = useState(false)

  const insertMergeField = (field: string) => {
    setForm((f) => ({ ...f, body_html: f.body_html + field }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{initial.name ? "Edytuj szablon" : "Nowy szablon email"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Name */}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Nazwa szablonu *
            </label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded border border-border bg-muted px-2.5 py-1.5 text-sm text-foreground"
              placeholder="np. Potwierdzenie oferty"
            />
          </div>

          {/* Category + Shared */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Kategoria
              </label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full rounded border border-border bg-muted px-2.5 py-1.5 text-sm text-foreground"
              >
                <option value="general">Ogólne</option>
                <option value="sales">Sprzedaż</option>
                <option value="support">Obsługa</option>
                <option value="followup">Follow-up</option>
              </select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={form.is_shared}
                  onChange={(e) => setForm({ ...form, is_shared: e.target.checked })}
                  className="rounded"
                />
                Współdzielony
              </label>
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Temat
            </label>
            <input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full rounded border border-border bg-muted px-2.5 py-1.5 text-sm text-foreground"
              placeholder="np. Oferta dla {{company.name}}"
            />
          </div>

          {/* Body HTML */}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">
                Treść *
              </label>
              <button
                onClick={() => setShowMergeFields(!showMergeFields)}
                className="text-[10px] hover:underline text-primary"
              >
                {showMergeFields ? "Ukryj zmienne" : "Wstaw zmienną"}
              </button>
            </div>

            {showMergeFields && (
              <div className="mb-2 flex flex-wrap gap-1 rounded border border-border bg-muted p-2">
                {MERGE_FIELDS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => insertMergeField(f.key)}
                    className="rounded px-1.5 py-0.5 text-[10px] transition-colors hover:opacity-80 text-primary bg-primary/10"
                    title={f.key}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            )}

            <textarea
              value={form.body_html}
              onChange={(e) => setForm({ ...form, body_html: e.target.value })}
              className="w-full rounded border border-border bg-muted px-2.5 py-1.5 text-sm font-mono text-foreground min-h-[200px] leading-normal"
              placeholder="<p>Szanowny/a {{contact.first_name}},</p><p>...</p>"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} size="sm">
            Anuluj
          </Button>
          <Button
            onClick={() => onSave(form)}
            disabled={isSaving || !form.name || !form.body_html}
            size="sm"
          >
            {isSaving ? "Zapisywanie..." : "Zapisz"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Preview Dialog ──────────────────────────────────────────────

function PreviewDialog({
  open,
  onOpenChange,
  template,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  template: EmailTemplate | null
}) {
  const renderMutation = useRenderEmailTemplate()
  const [rendered, setRendered] = useState<{ subject: string; body_html: string } | null>(null)

  const handleRender = async () => {
    if (!template) return
    try {
      const result = await renderMutation.mutateAsync({
        id: template.id,
        variables: {
          "company.name": "Przykładowa Firma Sp. z o.o.",
          "contact.first_name": "Jan",
          "contact.last_name": "Kowalski",
          "contact.email": "jan@firma.pl",
          "deal.name": "Nowy kontrakt",
          "deal.value": "50 000 zł",
          "user.name": "Anna Nowak",
          "user.email": "anna@okeycrm.pl",
        },
      })
      setRendered(result.data)
    } catch {
      toast.error("Błąd renderowania szablonu")
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Podgląd szablonu: {template?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {!rendered ? (
            <div className="text-center py-6">
              <Button onClick={handleRender} disabled={renderMutation.isPending} size="sm">
                <Eye size={14} className="mr-1.5" />
                {renderMutation.isPending ? "Renderowanie..." : "Wyrenderuj z przykładowymi danymi"}
              </Button>
            </div>
          ) : (
            <>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Temat
                </label>
                <p className="text-sm text-foreground">
                  {rendered.subject}
                </p>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Treść
                </label>
                <div
                  className="rounded border border-border bg-muted p-3 text-sm text-foreground"
                  dangerouslySetInnerHTML={{ __html: rendered.body_html }}
                />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); setRendered(null) }} size="sm">
            Zamknij
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Main Page ───────────────────────────────────────────────────

export function EmailTemplatesPage() {
  const [categoryFilter, setCategoryFilter] = useState("")
  const { data: templatesData, isLoading } = useEmailTemplates(
    categoryFilter ? { category: categoryFilter } : {}
  )
  const templates = templatesData?.data || []

  const createMutation = useCreateEmailTemplate()
  const updateMutation = useUpdateEmailTemplate()
  const deleteMutation = useDeleteEmailTemplate()

  const [formOpen, setFormOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null)
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null)

  const handleCreate = async (data: TemplateFormData) => {
    try {
      await createMutation.mutateAsync(data)
      setFormOpen(false)
      toast.success("Szablon utworzony")
    } catch {
      toast.error("Błąd tworzenia szablonu")
    }
  }

  const handleUpdate = async (data: TemplateFormData) => {
    if (!editingTemplate) return
    try {
      await updateMutation.mutateAsync({ id: editingTemplate.id, data })
      setEditingTemplate(null)
      toast.success("Szablon zaktualizowany")
    } catch {
      toast.error("Błąd aktualizacji szablonu")
    }
  }

  const handleDelete = async (template: EmailTemplate) => {
    if (!confirm(`Czy na pewno chcesz usunąć szablon "${template.name}"?`)) return
    try {
      await deleteMutation.mutateAsync(template.id)
      toast.success("Szablon usunięty")
    } catch {
      toast.error("Błąd usuwania szablonu")
    }
  }

  const handleDuplicate = async (template: EmailTemplate) => {
    try {
      await createMutation.mutateAsync({
        name: `${template.name} (kopia)`,
        subject: template.subject,
        body_html: template.body_html,
        body_text: template.body_text,
        category: template.category,
        is_shared: template.is_shared,
      })
      toast.success("Szablon zduplikowany")
    } catch {
      toast.error("Błąd duplikowania szablonu")
    }
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="page-title">
            Szablony email
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Twórz i zarządzaj szablonami wiadomości email ze zmiennymi
          </p>
        </div>
        <Button onClick={() => setFormOpen(true)} size="sm" className="gap-1.5">
          <Plus size={14} />
          Nowy szablon
        </Button>
      </div>

      {/* Category filter */}
      <div className="mb-4 flex gap-1">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            onClick={() => setCategoryFilter(cat.key)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              categoryFilter === cat.key
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground"
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Template list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
          <FileText size={40} className="text-muted-foreground" />
          <p className="mt-3 text-sm font-medium text-foreground">
            Brak szablonów
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Utwórz pierwszy szablon email, aby przyspieszyć pisanie wiadomości
          </p>
          <Button onClick={() => setFormOpen(true)} size="sm" className="mt-4 gap-1.5">
            <Plus size={14} />
            Utwórz szablon
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map((template) => (
            <div
              key={template.id}
              className="flex items-start justify-between rounded-lg border p-4 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium text-foreground">
                    {template.name}
                  </h3>
                  {template.category && (
                    <Badge variant="secondary" className="text-[10px]">
                      {CATEGORY_LABELS[template.category] || template.category}
                    </Badge>
                  )}
                  {template.is_shared && (
                    <Badge variant="outline" className="text-[10px]">
                      Współdzielony
                    </Badge>
                  )}
                </div>
                {template.subject && (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Temat: {template.subject}
                  </p>
                )}
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Użyty {template.usage_count}x
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0 ml-3">
                <button
                  onClick={() => setPreviewTemplate(template)}
                  className="flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-muted text-muted-foreground"
                  title="Podgląd"
                >
                  <Eye size={14} />
                </button>
                <button
                  onClick={() => handleDuplicate(template)}
                  className="flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-muted text-muted-foreground"
                  title="Duplikuj"
                >
                  <Copy size={14} />
                </button>
                <button
                  onClick={() => setEditingTemplate(template)}
                  className="flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-muted text-muted-foreground"
                  title="Edytuj"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => handleDelete(template)}
                  className="flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-muted text-muted-foreground"
                  title="Usuń"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <TemplateFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        initial={EMPTY_FORM}
        onSave={handleCreate}
        isSaving={createMutation.isPending}
      />

      {/* Edit dialog */}
      {editingTemplate && (
        <TemplateFormDialog
          open={true}
          onOpenChange={() => setEditingTemplate(null)}
          initial={{
            name: editingTemplate.name,
            subject: editingTemplate.subject || "",
            body_html: editingTemplate.body_html || "",
            body_text: editingTemplate.body_text || "",
            category: editingTemplate.category || "general",
            is_shared: editingTemplate.is_shared,
          }}
          onSave={handleUpdate}
          isSaving={updateMutation.isPending}
        />
      )}

      {/* Preview dialog */}
      <PreviewDialog
        open={!!previewTemplate}
        onOpenChange={() => setPreviewTemplate(null)}
        template={previewTemplate}
      />
    </div>
  )
}
